/**
 * liveScoringView.ts — pure helpers for the live scoring view (#8).
 *
 * While a session is RUNNING the console shows the current target note,
 * progress, and a cents needle in real time. These helpers turn a
 * `TargetMelody` + song-time position into display state. They are pure:
 * no React/native, deterministic, never throw — `atMs` may sit before the
 * first note or in a rest, and cents may be null/NaN, so each degrades safely.
 *
 * Scoring itself stays in the pure `@muzly/shared` core; this only renders it.
 */

import type { TargetMelody, TargetNote } from '@muzly/shared';
import { midiSolfege } from '@muzly/voice-engine';

/** Where we are in the melody at song-time `atMs`. */
export type MelodyProgress = {
  total: number;
  /** Notes whose onset has passed (1-based position), 0..total. */
  started: number;
  /** Active note index in `melody.notes`, or -1 during a rest / before start. */
  activeIndex: number;
  activeNote: TargetNote | null;
  nextNote: TargetNote | null;
  /** Time left in the active note (ms); 0 when no note is active. */
  remainingInNoteMs: number;
  /** Ms until the next onset during a rest/before-start, else null. */
  msToNextNote: number | null;
};

/** First note whose [startMs, startMs+durMs) span contains `atMs` (sorted). */
const activeIndexAt = (notes: TargetNote[], atMs: number): number => {
  for (let i = 0; i < notes.length; i++) {
    const n = notes[i];
    if (atMs < n.startMs) {
      return -1;
    } // sorted → no later note can match
    if (atMs < n.startMs + n.durMs) {
      return i;
    }
  }
  return -1;
};

/**
 * Resolve the current target note, progress, and timing at song-time `atMs`.
 * Pure/deterministic; assumes `melody.notes` is sorted (guaranteed post-parse).
 */
export const melodyProgress = (
  melody: TargetMelody,
  atMs: number,
): MelodyProgress => {
  const notes = melody.notes;
  const total = notes.length;
  const started = notes.filter((n) => atMs >= n.startMs).length;
  const activeIndex = activeIndexAt(notes, atMs);

  if (activeIndex >= 0) {
    const activeNote = notes[activeIndex];
    return {
      total,
      started,
      activeIndex,
      activeNote,
      nextNote: notes[activeIndex + 1] ?? null,
      remainingInNoteMs: Math.max(
        0,
        activeNote.startMs + activeNote.durMs - atMs,
      ),
      msToNextNote: null,
    };
  }

  // Rest / before-start: first note whose onset is still ahead.
  const nextNote = notes.find((n) => n.startMs > atMs) ?? null;
  return {
    total,
    started,
    activeIndex: -1,
    activeNote: null,
    nextNote,
    remainingInNoteMs: 0,
    msToNextNote: nextNote ? Math.max(0, nextNote.startMs - atMs) : null,
  };
};

/**
 * MIDI 번호 → 계이름(도/레/미…). null/비유한이면 '—'.
 *
 * 표 자체는 `@muzly/voice-engine`이 갖는다(`noteName`의 짝) — 측정 화면도 같은 계이름을
 * 쓰므로 표가 두 벌이면 한쪽만 고쳐질 자리가 생긴다. 여기 남는 건 콘솔에만 필요한
 * "값이 없으면 '—'" 규칙뿐이다.
 */
export const midiToSolfege = (midi: number | null | undefined): string =>
  typeof midi === 'number' && Number.isFinite(midi) ? midiSolfege(midi) : '—';

/**
 * Map a signed cents deviation onto a needle offset in [-100, 100]
 * (0 = on target, negative = flat/left, positive = sharp/right), clamped to
 * `rangeCents`. null/NaN ⇒ null (no reading). Pure.
 */
export const centsToOffsetPct = (
  cents: number | null | undefined,
  rangeCents = 100,
): number | null => {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) {
    return null;
  }
  const clamped = Math.max(-rangeCents, Math.min(rangeCents, cents));
  return (clamped / rangeCents) * 100;
};
